import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { t } from "@/lib/i18n";

export type ConfirmOptions = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button as destructive and moves focus to Cancel. */
  destructive?: boolean;
};

type ConfirmApi = {
  (options: ConfirmOptions): Promise<boolean>;
  /** Confirm + execute: the dialog stays open with locked buttons until the
   * action settles, so a slow network can't trick people into confirming twice.
   * Resolves the action's result, or null when cancelled/failed. */
  run<T>(options: ConfirmOptions, action: () => Promise<T>): Promise<T | null>;
};

const ConfirmContext = createContext<ConfirmApi | null>(null);

/**
 * Promise-based confirmation so call sites read top-to-bottom:
 *   if (!(await confirm({...}))) return;
 * Reserved for actions that are hard to notice or hard to reverse — anything
 * undoable should ship an Undo toast instead of a blocking prompt.
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const [pending, setPending] = useState(false);
  const resolverRef = useRef<((value: unknown) => void) | null>(null);
  const actionRef = useRef<(() => Promise<unknown>) | null>(null);

  const close = useCallback(() => {
    resolverRef.current = null;
    actionRef.current = null;
    setPending(false);
    setOptions(null);
  }, []);

  const settle = useCallback(async (value: boolean) => {
    const resolver = resolverRef.current;
    const action = actionRef.current;
    if (!value) {
      resolver?.(false);
      close();
      return;
    }
    // confirm(options) is the boolean-only variant. It deliberately has no
    // action callback, so a positive click must resolve true for the caller to
    // perform its mutation. Treating a missing action as cancellation made all
    // plain confirmations (including employee delete/archive) inert.
    if (!action) {
      resolver?.(true);
      close();
      return;
    }
    // Keep the dialog up while the mutation runs; the buttons lock so the
    // request cannot be fired twice from the same prompt.
    setPending(true);
    try {
      const result = await action();
      resolver?.(result === undefined ? true : result);
    } catch {
      resolver?.(null); // The caller's own onError reports the failure.
    } finally {
      close();
    }
  }, [close]);

  const open = useCallback(<T,>(next: ConfirmOptions, action?: () => Promise<T>): Promise<T | boolean> => {
    resolverRef.current?.(false); // A second confirm() silently cancels the first.
    actionRef.current = action ?? null;
    setOptions(next);
    return new Promise((resolve) => { resolverRef.current = resolve as (value: unknown) => void; });
  }, []);

  const api = useMemo<ConfirmApi>(() => Object.assign(
    ((next: ConfirmOptions) => open(next)) as ConfirmApi,
    {
      run: async <T,>(next: ConfirmOptions, action: () => Promise<T>): Promise<T | null> => {
        const result = await open(next, action) as T | boolean | null;
        return typeof result === "boolean" ? (result ? ((true as unknown) as T) : null) : result;
      },
    },
  ), [open]);

  return <ConfirmContext.Provider value={api}>
    {children}
    <Dialog open={Boolean(options)} onOpenChange={(isOpen) => { if (!isOpen && !pending) settle(false); }}>
      <DialogContent className="sm:max-w-md" onEscapeKeyDown={(event) => { if (pending) event.preventDefault(); }} onInteractOutside={(event) => { if (pending) event.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle>{options?.title}</DialogTitle>
          {options?.description ? <DialogDescription>{options.description}</DialogDescription> : null}
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" autoFocus={options?.destructive} disabled={pending} onClick={() => settle(false)}>{options?.cancelLabel ?? t("btn_cancel")}</Button>
          <Button type="button" variant={options?.destructive ? "destructive" : "default"} autoFocus={!options?.destructive} disabled={pending} onClick={() => settle(true)}>{options?.confirmLabel ?? t("btn_confirm")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </ConfirmContext.Provider>;
}

export function useConfirm(): ConfirmApi {
  const context = useContext(ConfirmContext);
  if (!context) throw new Error("useConfirm must be used inside ConfirmProvider");
  return context;
}
